import React, { useState, useEffect } from 'react';
import CardMCFilter from './CardMCFilter';
import { Button, Icon } from 'semantic-ui-react';
import * as XLSX from 'xlsx';
import CardMCVegaChart from './components/CardMCVegaChart';
import { VisualizationSpec } from 'vega-embed';

type ViewMode = 'noshow' | 'noshow3year' | 'cancel' | 'cancel3year';

const thaiMonths = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];
const thaiMonthsShort = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
];


type CardMCAppointmentSuccessRateProps = {
  // Add props if needed
  controller: any;
  runSequence: any;
  MeChartSequence: any;
}

const CardMCAppointmentSuccessRate = (props: CardMCAppointmentSuccessRateProps) => {
  // Filter state
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(1);
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(12);

  const [viewMode, setViewMode] = useState<ViewMode>('noshow');
  const [chartType, setChartType] = useState<'bar' | 'line'>('bar');
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [isTransposed, setIsTransposed] = useState(false);

  // Force bar on cancel views; force line on noshow3year
  useEffect(() => {
    if (viewMode === 'noshow3year') setChartType('line');
    if (viewMode === 'cancel' || viewMode === 'cancel3year') setChartType('bar');
  }, [viewMode]);

  // ─── Fetch ────────────────────────────────────────────────────────────────

  const fetchData = async () => {
    setLoading(true);
    try {

      let res = null
      let data = null
      if (props.MeChartSequence?.sequenceIndex === "Action") {
        let startDate = "";
        let endDate = "";
        let chart = ""
        if (viewMode === 'noshow') {
          const lastDay = new Date(year, endMonth, 0).getDate();
          startDate = `${year}-${String(month).padStart(2, '0')}-01 00:00:00`;
          endDate = `${year}-${String(endMonth).padStart(2, '0')}-${lastDay} 23:59:59`;
          chart = "appointment-success"
        } else if (viewMode === 'noshow3year') {
          startDate = `${endYear - 2}-01-01 00:00:00`;
          endDate = `${endYear}-12-31 23:59:59`;
          chart = "appointment-success"
        } else if (viewMode === 'cancel') {
          const lastDay = new Date(endYear, endMonth, 0).getDate();
          startDate = `${year}-${String(month).padStart(2, '0')}-01 00:00:00`;
          endDate = `${year}-${String(endMonth).padStart(2, '0')}-${lastDay} 23:59:59`;
          chart = "appointment-cancel"
        } else if (viewMode === 'cancel3year') {
          startDate = `${parseInt(String(endYear)) - 2}-01-01 00:00:00`;
          endDate = `${endYear}-12-31 23:59:59`;
          chart = "appointment-cancel-3year"
        }
        console.log('startDate: ', startDate);
        console.log('endDate: ', endDate);

        res = await props.runSequence({
          sequence: "MeChart",
          action: "QuerySQL",
          chart,
          startDate,
          endDate
        })

        console.log('res: ', res);
        if (res?.length > 0 && res?.[0]?.items && Array.isArray(res?.[0]?.items)) {
          console.log('data: ', data);
          data = res?.[0]?.items;
        }

        if (Array.isArray(data)) {
          setChartData(data);
        } else {
          setChartData([]);
        }
      }



      let url = '';

      if (viewMode === 'noshow') {
        const lastDay = new Date(year, endMonth, 0).getDate();
        const startDate = `${year}-${String(month).padStart(2, '0')}-01 00:00:00`;
        const endDate = `${year}-${String(endMonth).padStart(2, '0')}-${lastDay} 23:59:59`;
        url = `http://localhost:3002/api/appointment-success?startDate=${startDate}&endDate=${endDate}`;

      } else if (viewMode === 'noshow3year') {
        const startDate = `${endYear - 2}-01-01 00:00:00`;
        const endDate = `${endYear}-12-31 23:59:59`;
        url = `http://localhost:3002/api/appointment-success?startDate=${startDate}&endDate=${endDate}`;

      } else if (viewMode === 'cancel') {
        const lastDay = new Date(endYear, endMonth, 0).getDate();
        const startDate = `${year}-${String(month).padStart(2, '0')}-01 00:00:00`;
        const endDate = `${endYear}-${String(endMonth).padStart(2, '0')}-${lastDay} 23:59:59`;
        url = `http://localhost:3002/api/appointment-cancellation?startDate=${startDate}&endDate=${endDate}`;

      } else if (viewMode === 'cancel3year') {
        url = `http://localhost:3002/api/appointment-cancellation-3year?endYear=${endYear}`;
      }

      const response = await fetch(url);
      const sqlData = await response.json();
      console.log('sqlData: ', sqlData);

    } catch (error) {
      console.error('Failed to fetch data', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [viewMode]);

  const onSearch = () => fetchData();
  const onPrint = () => window.print();
  const onDownload = () => {
    if (!chartData.length) { alert('No data to download'); return; }

    const dataToExport = chartData.map((item: any) => ({ ...item }));

    if (isTransposed) {
      const headers = Object.keys(dataToExport[0]);
      const transposedData = [
        headers,
        ...dataToExport.map((row: any) => headers.map(h => row[h]))
      ];
      const pivotedData = transposedData[0].map((_: any, colIndex: number) =>
        transposedData.map(row => row[colIndex])
      );
      const ws = XLSX.utils.aoa_to_sheet(pivotedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Data');
      XLSX.writeFile(wb, `appointment_${viewMode}_Transposed_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } else {
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Data');
      XLSX.writeFile(wb, `appointment_${viewMode}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    }
  };

  // ─── Data Transformations ─────────────────────────────────────────────────

  /** Pic 1: No-show rate bar/line — one data point per month-year */
  const getNoshowData = () => {
    const agg: Record<string, { noShow: number; total: number; yearCe: number; monthIndex: number }> = {};
    chartData.forEach((row: any) => {
      const yCe = parseInt(row.year_ce);
      const mIndex = parseInt(row.month_index);
      const key = `${yCe}-${String(mIndex).padStart(2, '0')}`;
      if (!agg[key]) agg[key] = { noShow: 0, total: 0, yearCe: yCe, monthIndex: mIndex };
      if (parseInt(row.is_no_show) === 1) agg[key].noShow++;
      agg[key].total++;
    });

    return Object.values(agg)
      .sort((a, b) => (a.yearCe * 100 + a.monthIndex) - (b.yearCe * 100 + b.monthIndex))
      .map((item, i) => ({
        month: `${thaiMonthsShort[item.monthIndex - 1]} ${item.yearCe % 100}`,
        value: item.total > 0 ? parseFloat(((item.noShow / item.total) * 100).toFixed(2)) : 0,
        order: i
      }));
  };

  /** Pic 2: 3-year comparison — one line per Thai year */
  const getNoshow3YearData = () => {
    const agg: Record<string, { noShow: number; total: number }> = {};
    chartData.forEach((row: any) => {
      const yCe = parseInt(row.year_ce);
      const mIndex = parseInt(row.month_index);
      const key = `${yCe}-${String(mIndex).padStart(2, '0')}`;
      if (!agg[key]) agg[key] = { noShow: 0, total: 0 };
      if (parseInt(row.is_no_show) === 1) agg[key].noShow++;
      agg[key].total++;
    });

    return Object.entries(agg).map(([key, item]) => {
      const [yCe, mIdx] = key.split('-').map(Number);
      return {
        month: thaiMonths[mIdx - 1],
        year: `${yCe}`,
        value: item.total > 0 ? parseFloat(((item.noShow / item.total) * 100).toFixed(2)) : 0,
        order: mIdx
      };
    });
  };

  /**
   * Pic 3: Cancellation by month — stacked horizontal bar
   * Data shape from API: { year_ce, month_index, cancelled_by_patient, cancelled_by_staff }
   */
  const getCancelData = () => {
    const result: any[] = [];
    chartData
      .slice()
      .sort((a, b) => (parseInt(a.year_ce) * 100 + parseInt(a.month_index)) - (parseInt(b.year_ce) * 100 + parseInt(b.month_index)))
      .forEach((row: any, i: number) => {
        const label = thaiMonths[parseInt(row.month_index) - 1];
        result.push({ month: label, type: 'ยกเลิกโดยผู้รับบริการ', value: parseInt(row.cancelled_by_patient) || 0, order: i });
        result.push({ month: label, type: 'ยกเลิกโดยผู้ให้บริการ', value: parseInt(row.cancelled_by_staff) || 0, order: i });
      });
    return result;
  };

  /**
   * Pic 4: Cancellation 3-year — stacked vertical bar by year
   * Data shape from API: { year_ce, cancelled_by_patient, cancelled_by_staff }
   */
  const getCancel3YearData = () => {
    const result: any[] = [];
    chartData
      .slice()
      .sort((a, b) => parseInt(a.year_ce) - parseInt(b.year_ce))
      .forEach((row: any) => {
        const yr = `${parseInt(row.year_ce)}`;
        result.push({ year: yr, type: 'ยกเลิกโดยผู้รับบริการ', value: parseInt(row.cancelled_by_patient) || 0 });
        result.push({ year: yr, type: 'ยกเลิกโดยผู้ให้บริการ', value: parseInt(row.cancelled_by_staff) || 0 });
      });
    return result;
  };

  // ─── Vega Specs ───────────────────────────────────────────────────────────

  const getSpec = (): VisualizationSpec => {
    const cancelColors = { domain: ['ยกเลิกโดยผู้รับบริการ', 'ยกเลิกโดยผู้ให้บริการ'], range: ['#4285F4', '#EA4335'] };

    // ── Pic 1: No-show rate (bar / line) ──────────────────────────────────
    if (viewMode === 'noshow') {
      const data = getNoshowData();
      const markType = chartType === 'bar' ? 'bar' : 'line';
      const colorVal = '#EE82EE';
      return {
        width: 'container', height: 'container',
        autosize: { type: 'fit', contains: 'padding' },
        data: { values: data },
        mark: { type: markType, point: markType === 'line', tooltip: true },
        encoding: {
          [isTransposed && markType === 'bar' ? 'y' : 'x']: {
            field: 'month', type: 'ordinal',
            axis: { title: 'เดือน (Month)', labelAngle: isTransposed && markType === 'bar' ? 0 : -45 },
            sort: { field: 'order' }
          },
          [isTransposed && markType === 'bar' ? 'x' : 'y']: {
            field: 'value', type: 'quantitative',
            axis: { title: 'อัตราส่วน (%)', format: '.2f' }
          },
          color: { value: colorVal },
          tooltip: [
            { field: 'month', title: 'เดือน' },
            { field: 'value', title: 'อัตราส่วน (%)', format: '.2f' }
          ]
        }
      };
    }

    // ── Pic 2: No-show 3-year comparison (line only) ──────────────────────
    if (viewMode === 'noshow3year') {
      return {
        width: 'container', height: 'container',
        autosize: { type: 'fit', contains: 'padding' },
        data: { values: getNoshow3YearData() },
        mark: { type: 'line', point: true, tooltip: true },
        encoding: {
          x: { field: 'month', type: 'ordinal', axis: { title: 'เดือน (Month)', labelAngle: -45 }, sort: { field: 'order' } },
          y: { field: 'value', type: 'quantitative', axis: { title: 'อัตราส่วน (%)', format: '.2f' } },
          color: {
            field: 'year', type: 'nominal',
            scale: { scheme: 'set1' },
            legend: { title: 'ปี (Year)' }
          },
          tooltip: [
            { field: 'month', title: 'เดือน' },
            { field: 'year', title: 'ปี (Year)' },
            { field: 'value', title: 'อัตราส่วน (%)', format: '.2f' }
          ]
        }
      };
    }

    // ── Pic 3: Cancellation by month — stacked bar (horizontal default, vertical when transposed) ──
    if (viewMode === 'cancel') {
      return {
        width: 'container', height: 'container',
        autosize: { type: 'fit', contains: 'padding' },
        data: { values: getCancelData() },
        mark: { type: 'bar', tooltip: true },
        encoding: {
          [isTransposed ? 'x' : 'y']: {
            field: 'month', type: 'ordinal',
            axis: { title: 'เดือน (Month)', labelAngle: isTransposed ? -45 : 0 },
            sort: { field: 'order' }
          },
          [isTransposed ? 'y' : 'x']: {
            field: 'value', type: 'quantitative',
            axis: { title: 'จำนวนผู้ยกเลิกนัดหมาย (คน)' },
            stack: 'zero'
          },
          color: {
            field: 'type', type: 'nominal',
            scale: cancelColors,
            legend: { title: null }
          },
          tooltip: [
            { field: 'month', title: 'เดือน' },
            { field: 'type', title: 'ประเภท' },
            { field: 'value', title: 'จำนวน (คน)', format: ',' }
          ]
        }
      };
    }

    // ── Pic 4: Cancellation 3-year — stacked bar (vertical default, horizontal when transposed) ──
    return {
      width: 'container', height: 'container',
      autosize: { type: 'fit', contains: 'padding' },
      data: { values: getCancel3YearData() },
      mark: { type: 'bar', tooltip: true },
      encoding: {
        [isTransposed ? 'y' : 'x']: {
          field: 'year', type: 'ordinal',
          axis: { title: 'ปี (Year)', labelAngle: 0 }
        },
        [isTransposed ? 'x' : 'y']: {
          field: 'value', type: 'quantitative',
          axis: { title: 'จำนวนผู้ยกเลิกนัดหมาย (คน)' },
          stack: 'zero'
        },
        color: {
          field: 'type', type: 'nominal',
          scale: cancelColors,
          legend: { title: null }
        },
        tooltip: [
          { field: 'year', title: 'ปี พ.ศ.' },
          { field: 'type', title: 'ประเภท' },
          { field: 'value', title: 'จำนวน (คน)', format: ',' }
        ]
      }
    };
  };

  // ─── Filter mode per view ─────────────────────────────────────────────────

  const filterMode = () => {
    if (viewMode === 'noshow3year' || viewMode === 'cancel3year') return 'yearOnly' as const;
    if (viewMode === 'cancel' || viewMode === 'noshow') return 'oneYearMonth' as const;
    return 'default' as const;
  };

  const filterYear = (viewMode === 'noshow3year' || viewMode === 'cancel3year') ? endYear : year;
  const filterOnYearChange = (viewMode === 'noshow3year' || viewMode === 'cancel3year') ? setEndYear : setYear;

  // Chart-type buttons: noshow shows bar+line; 3-year noshow line only; cancel views bar only
  const showBarBtn = viewMode === 'noshow';
  const showLineBtn = viewMode === 'noshow' || viewMode === 'noshow3year';

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div id="card-mc-appointment-success-rate">
      {/* Row 1: View toggles */}
      <div className="controls-container">
        <div>
          <Button.Group>
            <Button
              active={viewMode === 'noshow'}
              style={viewMode === 'noshow' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setViewMode('noshow')}
            >
              อัตราการนัดหมายแล้วไม่มารับบริการ
            </Button>
            <Button
              active={viewMode === 'noshow3year'}
              style={viewMode === 'noshow3year' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setViewMode('noshow3year')}
            >
              อัตราฯ 3 ปีย้อนหลัง
            </Button>
            <Button
              active={viewMode === 'cancel'}
              style={viewMode === 'cancel' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setViewMode('cancel')}
            >
              จำนวนการยกเลิกนัดหมาย
            </Button>
            <Button
              active={viewMode === 'cancel3year'}
              style={viewMode === 'cancel3year' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setViewMode('cancel3year')}
            >
              จำนวนการยกเลิกฯ 3 ปีย้อนหลัง
            </Button>
          </Button.Group>
        </div>

        {/* Chart type buttons — only noshow shows both */}
        <div>
          <Button.Group>
            {showBarBtn && (
              <Button
                active={chartType === 'bar'}
                style={chartType === 'bar' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
                onClick={() => setChartType('bar')}
                icon
              >
                <Icon name='chart bar' /> Bar
              </Button>
            )}
            {showLineBtn && (
              <Button
                active={chartType === 'line'}
                style={chartType === 'line' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
                onClick={() => setChartType('line')}
                icon
              >
                <Icon name='chart line' /> Line
              </Button>
            )}
            {(viewMode === 'cancel' || viewMode === 'cancel3year') && (
              <Button
                active={true}
                style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }}
                icon
              >
                <Icon name='chart bar' /> Bar
              </Button>
            )}
          </Button.Group>
        </div>
      </div>

      {/* Row 2: Filter */}
      <div className="filter-container">
        <CardMCFilter
          mode={filterMode()}
          year={filterYear}
          month={month}
          endYear={endYear}
          endMonth={endMonth}
          onYearChange={filterOnYearChange}
          onMonthChange={setMonth}
          onEndYearChange={setEndYear}
          onEndMonthChange={setEndMonth}
          onSearch={onSearch}
          onPrint={onPrint}
        >
          <Button style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} size='small' icon labelPosition='left' onClick={onDownload}>
            <Icon name='download' />
            Download .xlsx
          </Button>
          {(viewMode === 'noshow' || viewMode === 'cancel' || viewMode === 'cancel3year') && (
            <Button
              style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }}
              size='small' icon labelPosition='left'
              onClick={() => setIsTransposed(!isTransposed)}
              disabled={viewMode === 'noshow' && chartType === 'line'}
              title='Transpose'
            >
              <Icon name='exchange' />
              Transpose
            </Button>
          )}
        </CardMCFilter>
      </div>

      {/* Row 4: Chart */}
      <div className="chart-wrapper">
        {loading
          ? <div>Loading...</div>
          : <CardMCVegaChart spec={getSpec()} actions={false} />
        }
      </div>
    </div>
  );
};

export default CardMCAppointmentSuccessRate;
